import {
  LAB_RESULT_CHECK_OUTCOME_LABEL,
  LAB_RESULT_OPS_QUEUE_LABEL,
  type LabResultCheck,
  type LabResultCheckOutcome,
  type WorkQueueItem,
} from "@care-by-ella/contracts";

// TES-1284 (TES-1269 point 6) — the queue card's "Kontrollera provsvar i
// Webdoc". Pure, so the rules are testable without a DOM.
//
// "Kökortet visar patientens uppgivna provtagningsdatum, senaste kontrollen och antal genomförda kontroller."

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Stockholm",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// The card's facts, one line each. Proposed staff labels.
export function labResultCheckLines(check: LabResultCheck): string[] {
  const lines = [
    `Uppgivet provtagningsdatum: ${
      check.sampleReportedAt ? fmtDateTime(check.sampleReportedAt) : "–"
    }`,
    `Senaste kontroll: ${
      check.lastCheckAt ? fmtDateTime(check.lastCheckAt) : "Ingen ännu"
    }`,
    `Antal kontroller: ${check.checkCount}`,
  ];
  if (check.state === "waiting" && check.nextCheckAt) {
    lines.push(`Nästa kontroll: ${fmtDateTime(check.nextCheckAt)}`);
  }
  if (check.problemReportedAt) {
    lines.push(
      `${LAB_RESULT_CHECK_OUTCOME_LABEL.problem}: ${fmtDateTime(
        check.problemReportedAt,
      )}`,
    );
  }
  return lines;
}

// Which of the three choices the card offers. While the item waits it
// offers none: it is out of the active list until the next check. Once
// escalated the result may still turn up, and "Problem med remiss eller
// provtagning" can still be recorded after three checks without one.
export function labResultCheckChoices(
  check: LabResultCheck,
): LabResultCheckOutcome[] {
  switch (check.state) {
    case "check":
    case "recheck":
      return ["results_available", "not_yet", "problem"];
    case "escalated":
      return check.problemReportedAt
        ? ["results_available"]
        : ["results_available", "problem"];
    case "waiting":
      return [];
  }
}

// "det lämnar Gör nu-listan": a check waiting for its next time is not
// something to do now, so it is not counted on the badge or the dashboard.
export function isWaitingLabCheck(item: WorkQueueItem): boolean {
  return item.labResultCheck?.state === "waiting";
}

// TES-1401 (Ö-473) — an escalated check has gone to the operational queue
// ("Saknade provsvar"), which checks the remiss, the sampling and the
// transfer. It leaves the doctor's active list and counts the same way; the
// doctor still sees it, under the list, and can register a result.
export function isEscalatedLabCheck(item: WorkQueueItem): boolean {
  return item.labResultCheck?.state === "escalated";
}

export function activeItems(items: WorkQueueItem[]): WorkQueueItem[] {
  return items.filter((i) => !isWaitingLabCheck(i) && !isEscalatedLabCheck(i));
}

// What the card says after a choice, until the list reloads.
export function labResultCheckDoneText(
  outcome: LabResultCheckOutcome,
  check: LabResultCheck | null,
): string {
  if (outcome === "results_available") {
    return "Provsvaret är registrerat. Ärendet går vidare när hela underlaget är klart.";
  }
  if (check?.state === "escalated") {
    return `Ärendet är markerat: Provsvar saknas – kontrollera provtagning eller remiss. Det har gått till kön ${LAB_RESULT_OPS_QUEUE_LABEL}.`;
  }
  return check?.nextCheckAt
    ? `Ärendet kommer tillbaka ${fmtDateTime(check.nextCheckAt)}.`
    : "Kontrollen är registrerad.";
}
